// The items seen on the belts (PRD section 17: "los objetos que se ven en las cintas son solo
// visuales"). The simulation only knows rates per minute; this module turns each belt's rate into
// items spaced along it, so what the player sees matches the figure: at a fixed belt speed, an item
// passes any point every 1/rate minutes. Items flow from cell to cell along chains of belts, with
// the phase measured from the end of each chain so they never jump between cells.
import type { ItemId } from '../sim/data';
import { EPSILON } from '../sim/flow';
import { across, keyOf, opposite, type Cell, type Side } from '../sim/geometry';
import type { BeltReport, PieceStatus } from '../sim/report';

/** How fast items ride a belt, in cells per minute of game time. */
export const BELT_SPEED = 30;
/** Gap between items queued on a belt whose exit is full, in cells. */
export const QUEUE_GAP = 0.5;

export interface BeltTrack {
  readonly key: string;
  readonly cell: Cell;
  /** Side items come in through, and side they leave through. */
  readonly entry: Side;
  readonly exit: Side;
  /** Cells from this belt's entry edge to the end of its chain, where items leave the belts. */
  readonly toEnd: number;
  readonly rate: number;
  /** The item it carries most of, if it carries anything. */
  readonly item?: ItemId;
  readonly status: PieceStatus;
}

export interface ItemPose {
  readonly item: ItemId;
  /** Position in cell units: (0.5, 0.5) is the centre of the first cell. */
  readonly x: number;
  readonly y: number;
  /** Heading in radians: 0 points to +x (east), π/2 to +y (south). */
  readonly heading: number;
}

const STEP: Readonly<Record<Side, readonly [number, number]>> = { N: [0, -1], E: [1, 0], S: [0, 1], W: [-1, 0] };

/** Lays the belts of an interior out as tracks: where items enter each cell and how far they still ride. */
export function beltTracks(belts: readonly BeltReport[]): BeltTrack[] {
  const byKey = new Map(belts.map((b) => [b.key, b]));
  const next = (b: BeltReport): BeltReport | undefined => {
    const to = across({ cell: b.cell, side: b.exit });
    const n = byKey.get(keyOf(to.cell));
    // The next belt takes the items only if they come in through one of its input sides.
    return n && n.exit !== to.side && (n.inputs.length === 0 || n.inputs.includes(to.side)) ? n : undefined;
  };
  const toEnd = new Map<string, number>();
  const measure = (b: BeltReport): number => {
    const known = toEnd.get(b.key);
    if (known !== undefined) return known;
    // Mark first, so a loop of belts ends instead of recursing forever.
    toEnd.set(b.key, 1);
    const n = next(b);
    const value = n ? 1 + measure(n) : 1;
    toEnd.set(b.key, value);
    return value;
  };
  return belts.map((b) => {
    const item = [...b.carrying].sort((x, y) => y.amount - x.amount)[0]?.item;
    return {
      key: b.key,
      cell: b.cell,
      entry: b.inputs.find((s) => s !== b.exit) ?? opposite(b.exit),
      exit: b.exit,
      toEnd: measure(b),
      rate: b.rate,
      ...(item ? { item } : {}),
      status: b.status,
    };
  });
}

/** Where a point `p` (0 at the entry edge, 1 at the exit edge) of a belt cell is, and which way it heads. */
function along(track: BeltTrack, p: number): { x: number; y: number; heading: number } {
  const [col, row] = track.cell;
  const cx = col + 0.5;
  const cy = row + 0.5;
  // Items ride from the entry edge to the centre, then from the centre to the exit edge.
  const [ix, iy] = STEP[track.entry];
  const [ox, oy] = STEP[track.exit];
  if (p < 0.5) {
    const k = 0.5 - p;
    return { x: cx + ix * k, y: cy + iy * k, heading: Math.atan2(-iy, -ix) };
  }
  const k = p - 0.5;
  return { x: cx + ox * k, y: cy + oy * k, heading: Math.atan2(oy, ox) };
}

/**
 * Every item on the belts at `minutes` of game time. A belt with a rate shows items spaced by
 * speed ÷ rate, moving at BELT_SPEED; a belt whose exit is full and that moves nothing shows its
 * items queued and still; an idle belt shows none.
 */
export function itemPoses(tracks: readonly BeltTrack[], minutes: number): ItemPose[] {
  const poses: ItemPose[] = [];
  for (const track of tracks) {
    if (!track.item) continue;
    const moving = track.rate > EPSILON;
    if (!moving && track.status !== 'blocked') continue;
    const gap = moving ? BELT_SPEED / track.rate : QUEUE_GAP;
    const travelled = moving ? BELT_SPEED * minutes : 0;
    // Items sit where (distance to the end + distance travelled) is a whole number of gaps.
    const near = track.toEnd - 1;
    const phase = ((travelled % gap) + gap) % gap;
    for (let k = Math.ceil((near + phase) / gap); k * gap - phase < track.toEnd; k++) {
      const u = k * gap - phase;
      if (u < near) continue;
      const pose = along(track, track.toEnd - u);
      poses.push({ item: track.item, ...pose });
    }
  }
  return poses;
}
